import { useBoxRenewalError } from './useBoxRenewalError';
import { resolveActionCards } from './resolveActionCards';
import { hasPremiumFeature } from '@lub0v/santa-utils';
import type { BoxInfo, CardInfo, PremiumDrawUsage } from '@lub0v/santa-utils';
import { useTranslation } from 'react-i18next';
import { showDialog, showToast } from '@src/components';
import { createElement } from 'react';
import { confirmBoxDraw, previewBoxDraw, type DrawAssignment } from './draw';
import { DrawPreview, DrawReadyTitle } from './DrawPreview';

export function useConfirmBoxDraw(
  info: BoxInfo,
  source: CardInfo[] | (() => Promise<CardInfo[]>),
  onChanged: () => Promise<void> | void,
) {
  const { t } = useTranslation();
  const handleRenewalError = useBoxRenewalError(info);
  const box = info.box;
  const archived = Boolean(box.isArchived);
  return async function confirmDraw() {
    const cards = await resolveActionCards(source);
    if (!cards) return;
    if (!info.isAdmin || info.isActiveDraw || archived) return;
    if (cards.filter((card) => card.username).length < 3) {
      showDialog({
        title: t('game.draw.needPeopleTitle'),
        children: t('game.draw.needPeople'),
        submit: t('box.close'),
      });
      return;
    }
    const premiumUsage = new Set<PremiumDrawUsage>(hasPremiumFeature(box, 'boxDraw') ? ['initial_preview'] : []);
    let assignments: DrawAssignment[] | null;
    try {
      assignments = hasPremiumFeature(box, 'boxDraw') ? previewBoxDraw(cards, Boolean(box.useCircleDraw)) : null;
    } catch {
      showToast(t('game.draw.failed'));
      return;
    }
    showDialog({
      title: createElement(DrawReadyTitle),
      renderBody: (busy, setSubmitDisabled, setSubmitSummary) =>
        createElement(DrawPreview, {
          cards,
          circleDraw: Boolean(box.useCircleDraw),
          initial: assignments || [],
          premium: Boolean(hasPremiumFeature(box, 'boxDraw')),
          onChange: (next) => {
            assignments = next;
          },
          onPremiumUsage: (usage) => { premiumUsage.add(usage); },
          onSaveSummaryChange: setSubmitSummary,
          disabled: busy, onIncompleteChange: setSubmitDisabled,
        }),
      cancel: t('box.cancel'),
      submit: t('game.draw.confirmResults'),
      mobileFullScreen: true,
      onSubmit: async () => {
        if (assignments?.some(pair => !pair.wardId)) { showToast(t('game.draw.manual.incomplete')); throw new Error('Incomplete draw'); }
        const error = await confirmBoxDraw(assignments, Boolean(box.useCircleDraw), box.key, [...premiumUsage]);
        if (error) {
          if (!handleRenewalError(error)) showToast(t(error === 'RANDOMIZER_FAILED' ? 'game.draw.failed' : error));
          throw new Error(error);
        }
        await onChanged();
      },
      testId: 'game-draw-dialog',
    });
  };
}
